import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Plus, Star } from 'lucide-react';
import apiClient from '../config/axios';
import { vttConfigured } from '../config/vtt';
import OmniSearch from './OmniSearch';
import BuildRulesTile from './BuildRulesTile';
import { roleLabel } from './InviteList';
import { crestInitials } from '../utils/wikiCategories';
import TryTabletopButton, { PracticeBadge } from './TryTabletopButton';
import {
  campaignHref, needsFullLoad, mayOpenTable, ownTab, tableHref, wikiHref, wikiLinkProps,
} from '../utils/campaignLinks';

/**
 * The returning player's homepage (issue 1587, slice 2): a greeting, their
 * default campaign (issue 1540) with its tabletop and wiki, their campaigns,
 * characters and recent look-ups, then the rules and the DM tools.
 *
 * Five requests, in parallel: the games listing (which carries each campaign's
 * next session, players and recaps), the wikis listed to me (whether a Wiki
 * button leads anywhere), the characters, the recently viewed (which says
 * which are favourites), and the feed (slice 3: "Since you were last here",
 * with each campaign's counts of newly revealed, Talk replies and, for the DM,
 * loose ends). No count of the rules.
 *
 * Every section that would be empty is hidden, except the default-campaign
 * card, which becomes a prompt; the feed says "all caught up" only right
 * after Mark all read.
 */

// The rules row: the six most-used kinds, then the rest as "Also" links.
export const TOP_SEGMENTS = ['spells', 'feats', 'classes', 'monsters', 'items', 'races'];
const LIST_MAX = 4;
const RECENT_MAX = 8;
const DAY = 86400000;

const fullDate = (d) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const sessionWhen = (iso) => new Date(iso).toLocaleString('en-US', {
  weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

/** Whole days from today to `iso` by the reader's calendar (0 today, 1 tomorrow). */
export function daysUntil(iso, now = new Date()) {
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const t = new Date(iso);
  const b = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  return Math.round((b - a) / DAY);
}

/** A session still to come: today's counts until the day ends, even once its start time has passed. */
export const stillToCome = (at, now = new Date()) => !!at && daysUntil(at, now) >= 0;

/** The soonest session still to come across my campaigns, with its campaign, or null. */
export function soonestSession(campaigns, now = new Date()) {
  let best = null;
  for (const c of campaigns || []) {
    const at = c.next_session && c.next_session.at;
    if (!stillToCome(at, now)) continue;
    if (!best || new Date(at) < new Date(best.at)) best = { at, campaign: c };
  }
  return best;
}

/**
 * The greeting's one sentence, from real data: the next session, then what was
 * revealed since I last looked (`counts`: the feed's per-campaign counts), in
 * the default campaign, else wherever the most was.
 */
export function summaryParts(campaigns, defaultId, now = new Date(), counts = null) {
  const parts = [];
  const next = soonestSession(campaigns, now);
  if (next) {
    const n = daysUntil(next.at, now);
    const when = n <= 0 ? 'today' : n === 1 ? 'tomorrow' : `in ${n} days`;
    const where = campaigns.length > 1 && next.campaign.id !== defaultId ? `, in ${next.campaign.name}` : '';
    parts.push(`Next session ${when}${where}.`);
  }
  if (counts) {
    const revealed = (c) => (counts[c.id] && counts[c.id].revealed_unread) || 0;
    const mine = (campaigns || []).find((c) => c.id === defaultId);
    const top = mine && revealed(mine) > 0 ? mine
      : [...(campaigns || [])].sort((a, b) => revealed(b) - revealed(a))[0];
    const n = top ? revealed(top) : 0;
    if (n > 0) parts.push(`${n === 1 ? 'One thing was' : `${n} things were`} revealed in ${top.name} since you last looked.`);
  }
  return parts;
}

/**
 * The count line on the default-campaign card, from the feed's counts for it:
 * newly revealed (#1570), Talk replies (#1569) and, for the DM, loose ends
 * (#1568), each hidden at zero. Until the feed answers, the games listing's
 * "New in the wiki".
 */
export function campaignCounts(c, counts) {
  if (!c) return [];
  if (!counts) return c.wiki_unread ? ['New in the wiki'] : [];
  return [
    counts.revealed_unread > 0 ? `${counts.revealed_unread} newly revealed` : null,
    counts.talk_unread > 0 ? plural(counts.talk_unread, 'Talk reply', 'Talk replies') : null,
    counts.loose_ends > 0 ? plural(counts.loose_ends, 'loose end') : null,
  ].filter(Boolean);
}

/** A feed item's line: what happened, on which page. */
export function feedText(it) {
  const title = it.page ? it.page.title : '';
  if (it.kind === 'reveal') return it.field ? `Revealed: ${it.field} on ${title}` : `Revealed: ${title}`;
  if (it.kind === 'talk') return it.notice === 'made_public' ? `Your private thread on ${title} was made public` : `${it.actor} replied on ${title}`;
  if (it.kind === 'session') {
    if (it.status === 'planned') return `Session ${it.number} planned: ${title}`;
    return it.recap ? `Recap of session ${it.number}: ${title}` : `Session ${it.number} played: ${title}`;
  }
  return title;
}

/** "just now", "5 minutes ago", "3 hours ago", "2 days ago". */
export function ago(iso, now = new Date()) {
  const m = Math.max(0, Math.round((now - new Date(iso)) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${plural(m, 'minute')} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${plural(h, 'hour')} ago`;
  return `${plural(Math.round(h / 24), 'day')} ago`;
}

/** "Human · Wizard 5 / Fighter 2", or the level alone, or nothing. */
export function characterLine(ch) {
  const classes = (ch.classes || []).map((k) => (k.level ? `${k.name} ${k.level}` : k.name)).join(' / ');
  const build = classes || (ch.level ? `Level ${ch.level}` : '');
  return [ch.race, build].filter(Boolean).join(' · ');
}

const campaignOf = (ch) => (ch.parties && ch.parties[0] ? ch.parties[0].campaign_name : null);

/** A link to a campaign page, by a full load when the campaign is of another system. */
const CampaignLink = ({ c, children, ...rest }) => (needsFullLoad(c)
  ? <a href={campaignHref(c)} {...rest}>{children}</a>
  : <Link to={campaignHref(c)} {...rest}>{children}</Link>);

function WikiButton({ wiki, className, label, text }) {
  const navigate = useNavigate();
  if (!wiki) return null;
  return (
    <a
      {...wikiLinkProps(navigate, wiki)}
      className={className}
      aria-label={label}
    >
      {text}
    </a>
  );
}

function DefaultCampaignCard({ games, wikis, feed, onChoose }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  if (!games) {
    return <section className="dash-card dash-default dash-skeleton" aria-busy="true" aria-label="Your default campaign" data-testid="dash-default-loading" />;
  }
  const { campaigns: listed = [], invites = [], default_campaign: defaultId } = games;
  // A practice campaign (qa-runs 1163) is never the main campaign by itself, nor offered as one to choose.
  const campaigns = listed.filter((x) => !x.practice);
  const c = listed.find((x) => x.id === defaultId);

  if (!campaigns.length) {
    return (
      <section className="dash-card dash-default dash-prompt" aria-labelledby="dash-default-h" data-testid="dash-default-none">
        <h2 id="dash-default-h" className="dash-h2">Start or join a campaign</h2>
        <p>A campaign gathers your players, its sessions and a wiki of its world. Start your own, or accept an invitation from your DM.</p>
        <div className="home-ctas">
          <Link to="/campaigns" className="home-cta home-cta-gold">Start a campaign</Link>
          <Link to="/campaigns" className="home-cta home-cta-outline">
            {invites.length ? `${plural(invites.length, 'invitation')} waiting` : 'Join with an invitation'}
          </Link>
          <TryTabletopButton />
        </div>
      </section>
    );
  }

  if (!c) {
    const choose = async (id) => {
      if (!id) return;
      setSaving(true);
      setError('');
      try {
        await apiClient.put('/api/users/me/default-campaign', { campaign_id: id });
        onChoose(id);
      } catch (e) {
        setError('That could not be saved just now. Try again in a moment.');
      } finally {
        setSaving(false);
      }
    };
    return (
      <section className="dash-card dash-default dash-prompt" aria-labelledby="dash-default-h" data-testid="dash-default-choose">
        <h2 id="dash-default-h" className="dash-h2">Choose your main campaign</h2>
        <p>It sits here with its next session, its tabletop and its wiki, one click away.</p>
        <label className="dash-choose">
          <span>Default campaign</span>
          <select className="input" defaultValue="" disabled={saving} onChange={(e) => choose(e.target.value)}>
            <option value="" disabled>Choose a campaign…</option>
            {campaigns.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.system_name})</option>)}
          </select>
        </label>
        {error && <p className="dash-error" role="alert">{error}</p>}
      </section>
    );
  }

  const wiki = (wikis || []).find((w) => w.id === c.id);
  const counts = campaignCounts(c, feed && feed.campaigns ? feed.campaigns[c.id] || {} : null);
  const next = c.next_session && stillToCome(c.next_session.at) ? c.next_session : null;
  return (
    <section className="dash-card dash-default" aria-labelledby="dash-default-h" data-testid="dash-default">
      <div className="dash-default-head">
        <span className="dash-crest" aria-hidden="true">{crestInitials(c.name)}</span>
        <div className="dash-default-title">
          <p className="dash-kicker">Your default campaign</p>
          <h2 id="dash-default-h" className="dash-h2 [overflow-wrap:anywhere]">{c.name}</h2>
          <p className="dash-meta">
            {[c.system_name, roleLabel(c.role), c.player_count > 0 ? plural(c.player_count, 'player') : null,
              c.recap_count > 0 ? plural(c.recap_count, 'session recap') : null].filter(Boolean).join(' · ')}
          </p>
        </div>
        <CampaignLink c={c} className="dash-more" aria-label={`Campaign page of ${c.name}`}>Campaign ⋯</CampaignLink>
      </div>
      <div className="dash-next" data-testid="dash-next">
        <span className="dash-next-label">Next session</span>
        {next
          ? <span>{sessionWhen(next.at)}{next.note ? ` · ${next.note}` : ''}</span>
          : <span className="dash-muted">Not scheduled yet</span>}
      </div>
      {counts.length > 0 && (
        <ul className="dash-counts" data-testid="dash-counts">
          {counts.map((t) => <li key={t}><span className="wiki-new-marker" aria-hidden="true" /> {t}</li>)}
        </ul>
      )}
      <div className="home-ctas dash-ctas">
        {vttConfigured() && mayOpenTable(c) && (
          <Link to={tableHref(c.id)} {...ownTab()} className="home-cta home-cta-gold">Open the tabletop</Link>
        )}
        <WikiButton wiki={wiki} className="home-cta home-cta-outline" label={`Open the wiki of ${c.name}`} text="Open the wiki" />
      </div>
    </section>
  );
}

/** A wiki page of a campaign, by a full load when the campaign is of another system. */
function FeedLink({ it, children }) {
  const href = `${wikiHref(it.campaign)}&page=${encodeURIComponent(it.page.slug)}`;
  return needsFullLoad(it.campaign)
    ? <a href={href} {...ownTab()} className="dash-row-name">{children}</a>
    : <Link to={href} {...ownTab()} className="dash-row-name">{children}</Link>;
}

/** How many feed rows show at first, and how many more each "Show more" adds (Tom's decision A on #1587). */
export const FEED_SHOWN = 10;

function SinceLastHere({ feed, caughtUp, onMarkRead }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [shown, setShown] = useState(FEED_SHOWN);
  if (!feed) return null;
  const items = feed.items || [];
  if (!items.length && !caughtUp) return null;
  const markRead = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await apiClient.put('/api/users/me/feed/read', {});
      onMarkRead(res.data);
    } catch (e) {
      setError('That could not be saved just now. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="dash-card" aria-labelledby="dash-feed-h" data-testid="dash-feed">
      <SectionHead
        id="dash-feed-h"
        title="Since you were last here"
        more={items.length > 0 && <button type="button" className="dash-link" onClick={markRead} disabled={busy}>Mark all read</button>}
      />
      {items.length > 0 ? (
        <ul className="dash-rows">
          {items.slice(0, shown).map((it) => (
            <li key={it.id} className="dash-row" data-testid="dash-feed-item">
              <span className="dash-row-text">
                <FeedLink it={it}>{feedText(it)}</FeedLink>
                <span className="dash-row-meta">{it.campaign.name} · {ago(it.at)}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : <p className="dash-muted" role="status">You’re all caught up.</p>}
      {items.length > shown && (
        <button type="button" className="dash-link" data-testid="dash-feed-more" onClick={() => setShown((n) => n + FEED_SHOWN)}>
          Show more ({items.length - shown})
        </button>
      )}
      {error && <p className="dash-error" role="alert">{error}</p>}
    </section>
  );
}

function SectionHead({ id, title, more }) {
  return (
    <div className="dash-section-head">
      <h2 id={id} className="dash-h2 dash-h2-sm">{title}</h2>
      {more}
    </div>
  );
}

function CampaignList({ games, wikis }) {
  if (!games) return <div className="dash-card dash-skeleton dash-skeleton-list" aria-hidden="true" />;
  const others = (games.campaigns || []).filter((c) => c.id !== games.default_campaign);
  if (!others.length) return null;
  const withTable = vttConfigured();
  return (
    <section className="dash-card" aria-labelledby="dash-campaigns-h" data-testid="dash-campaigns">
      <SectionHead id="dash-campaigns-h" title="Your campaigns" more={<Link to="/campaigns" className="dash-link">See all <ArrowRight aria-hidden="true" /></Link>} />
      <ul className="dash-rows">
        {others.slice(0, LIST_MAX).map((c) => {
          const next = c.next_session && stillToCome(c.next_session.at) ? c.next_session : null;
          return (
            <li key={c.id} className="dash-row" data-testid={`dash-campaign-${c.id}`}>
              <span className="dash-crest dash-crest-sm" aria-hidden="true">{crestInitials(c.name)}</span>
              <span className="dash-row-text">
                <CampaignLink c={c} className="dash-row-name">{c.name}</CampaignLink><PracticeBadge campaign={c} />
                <span className="dash-row-meta">
                  {[c.system_name, roleLabel(c.role), next ? `next ${sessionWhen(next.at)}` : null].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="dash-row-actions">
                {withTable && mayOpenTable(c) && (
                  <Link to={tableHref(c.id)} {...ownTab()} className="dash-btn" aria-label={`Tabletop of ${c.name}`}>Tabletop</Link>
                )}
                <WikiButton wiki={(wikis || []).find((w) => w.id === c.id)} className="dash-btn" label={`Wiki of ${c.name}`} text="Wiki" />
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function CharacterList({ characters }) {
  if (!characters) return <div className="dash-card dash-skeleton dash-skeleton-list" aria-hidden="true" />;
  if (!characters.length) return null;
  return (
    <section className="dash-card" aria-labelledby="dash-characters-h" data-testid="dash-characters">
      <SectionHead id="dash-characters-h" title="Your characters" more={<Link to="/characters" className="dash-link"><Plus aria-hidden="true" /> New character</Link>} />
      <ul className="dash-rows">
        {characters.slice(0, LIST_MAX).map((ch) => {
          const line = characterLine(ch);
          const where = campaignOf(ch);
          return (
            <li key={ch.id} className="dash-row" data-testid={`dash-character-${ch.id}`}>
              <span className="dash-row-text">
                <span className="dash-row-name">{ch.name}</span>
                <span className="dash-row-meta">{[line, where].filter(Boolean).join(' · ') || 'Not built yet'}</span>
              </span>
              <span className="dash-row-actions">
                <Link to={`/characters?id=${encodeURIComponent(ch.id)}`} className="dash-btn" aria-label={`Sheet of ${ch.name}`}>Sheet</Link>
              </span>
            </li>
          );
        })}
      </ul>
      {characters.length > LIST_MAX && <Link to="/characters" className="dash-link dash-see-all">See all {characters.length}</Link>}
    </section>
  );
}

function RecentChips({ recent }) {
  if (!recent) return <div className="dash-card dash-skeleton dash-skeleton-chips" aria-hidden="true" />;
  const named = recent.filter((v) => v.name).slice(0, RECENT_MAX);
  if (!named.length) return null;
  return (
    <section className="dash-card" aria-labelledby="dash-recent-h" data-testid="dash-recent">
      <SectionHead id="dash-recent-h" title="Recently looked up" more={<Link to="/favorites" className="dash-link">Favourites</Link>} />
      <ul className="home-chips">
        {named.map((v) => (
          <li key={`${v.entity_type}/${v.slug}`}>
            <Link className="home-chip" to={`/${v.entity_type}?slug=${encodeURIComponent(v.slug)}`} state={{ from: '/' }}>
              {v.favorite && <Star className="dash-star" aria-label="Favourite" role="img" />}
              {v.name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RulesRow({ tiles, tools }) {
  const top = TOP_SEGMENTS.map((s) => tiles.find((t) => t.segment === s)).filter(Boolean);
  const rest = tiles.filter((t) => !top.includes(t));
  return (
    <>
      {top.length > 0 && (
        <section className="dash-section" aria-labelledby="dash-rules-h" data-testid="dash-rules">
          <h2 id="dash-rules-h" className="home-group-name">The rules</h2>
          <ul className="dash-rules">
            {top.map((t) => (
              <li key={t.href}>
                <Link to={t.href} className="home-tile dash-rule" data-testid={`dash-rule-${t.segment}`}>
                  <span className="home-ring home-ring-sm" aria-hidden="true"><t.icon /></span>
                  <span className="home-tile-name">{t.name}</span>
                </Link>
              </li>
            ))}
          </ul>
          {rest.length > 0 && (
            <p className="dash-also" data-testid="dash-also">
              Also:{' '}
              {rest.map((t, i) => (
                <React.Fragment key={t.href}>
                  {i > 0 && ' · '}
                  <Link to={t.href}>{t.name}</Link>
                </React.Fragment>
              ))}
            </p>
          )}
        </section>
      )}
      <section className="dash-section" aria-labelledby="dash-tools-h" data-testid="dash-tools">
        <h2 id="dash-tools-h" className="home-group-name">DM tools</h2>
        <ul className="home-tiles home-tiles-tools" style={{ '--tool-count': tools.length }}>
          {tools.map((t) => (
            <li key={t.href}>
              <Link to={t.href} className="home-tile">
                <span className="home-ring" aria-hidden="true"><t.icon /></span>
                <span className="home-tile-text">
                  <span className="home-tile-name">{t.name}</span>
                  <span className="home-tile-desc">{t.description}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
      {/* The rule builder (issue 375). */}
      <BuildRulesTile sectionClass="dash-section" />
    </>
  );
}

const listOf = (res, key) => {
  const d = res && res.data;
  if (key) return (d && Array.isArray(d[key])) ? d[key] : [];
  return Array.isArray(d) ? d : [];
};

export default function HomeDashboard({ user, tiles, tools, placeholder, searchRef }) {
  const [games, setGames] = useState(null);
  const [wikis, setWikis] = useState(null);
  const [characters, setCharacters] = useState(null);
  const [recent, setRecent] = useState(null);
  const [feed, setFeed] = useState(null);
  const [caughtUp, setCaughtUp] = useState(false);

  useEffect(() => {
    let live = true;
    const settle = (p, set, pick) => p.then((r) => { if (live) set(pick(r)); }).catch(() => { if (live) set(pick(null)); });
    settle(apiClient.get('/api/users/me/games'), setGames, (r) => (r && r.data) || { campaigns: [], invites: [], default_campaign: null });
    settle(apiClient.get('/api/users/me/wikis'), setWikis, (r) => listOf(r, 'wikis'));
    settle(apiClient.get('/api/characters'), setCharacters, (r) => listOf(r));
    settle(apiClient.get('/api/recently-viewed'), setRecent, (r) => listOf(r));
    settle(apiClient.get('/api/users/me/feed'), setFeed, (r) => (r && r.data && Array.isArray(r.data.items) ? r.data : null));
    return () => { live = false; };
  }, []);

  const name = (user && (user.display_name || (user.handle ? `@${user.handle}` : null))) || null;
  const summary = games ? summaryParts(games.campaigns || [], games.default_campaign, new Date(), feed && feed.campaigns).join(' ') : '';
  const onChoose = (id) => setGames((g) => ({ ...g, default_campaign: id }));
  const onMarkRead = (fresh) => {
    setFeed(fresh && Array.isArray(fresh.items) ? fresh : { items: [], campaigns: {} });
    setCaughtUp(true);
    setGames((g) => (g ? { ...g, campaigns: (g.campaigns || []).map((c) => ({ ...c, wiki_unread: false })) } : g));
  };

  return (
    <div className="home dash" data-testid="home-dashboard">
      <section className="dash-greet" aria-labelledby="dash-title">
        <div className="dash-greet-text">
          <p className="dash-date" data-testid="dash-date">{fullDate(new Date())}</p>
          <h1 id="dash-title" className="dash-welcome">{name ? `Welcome back, ${name}.` : 'Welcome back.'}</h1>
          <p className="dash-summary" data-testid="dash-summary">{summary || ' '}</p>
        </div>
        <div className="home-search dash-search" data-testid="home-search">
          <OmniSearch large idPrefix="home-search" inputRef={searchRef} ariaLabel="Search the rules" placeholder={placeholder} />
          <kbd className="home-search-key" aria-hidden="true">/</kbd>
        </div>
      </section>

      <div className="dash-grid">
        <div className="dash-col">
          <DefaultCampaignCard games={games} wikis={wikis} feed={feed} onChoose={onChoose} />
          <CampaignList games={games} wikis={wikis} />
        </div>
        <div className="dash-col">
          <SinceLastHere feed={feed} caughtUp={caughtUp} onMarkRead={onMarkRead} />
          <CharacterList characters={characters} />
          <RecentChips recent={recent} />
        </div>
      </div>

      <RulesRow tiles={tiles} tools={tools} />
    </div>
  );
}
